function initPredictiveSearchProductSliders(root = document) {
  root.querySelectorAll('.predictive-search-products-slider').forEach((wrapper) => {
    if (wrapper.dataset.predictiveSwiperInitialized === 'true') {
      return;
    }

    const sliderEl = wrapper.querySelector('.carousel-slider-product');
    const prevButton = wrapper.querySelector('.predictive-search-nav-prev');
    const nextButton = wrapper.querySelector('.predictive-search-nav-next');

    if (!sliderEl) {
      return;
    }

    function updateNavigationButtons(swiperInstance) {
      // Navigation arrows stay always enabled; no disabled state handling
      return;
    }

    const predictiveSwiper = new Swiper(sliderEl, {
      slidesPerView: 3,
      spaceBetween: 10,
      loop: false,
      // Allow normal click navigation on product cards
      preventClicks: false,
      preventClicksPropagation: false,
      mousewheel: {
        enabled: true,
        forceToAxis: true,
        sensitivity: 1,
        releaseOnEdges: false,
      },
      breakpoints: {
         1200: { slidesPerView: 2.2, spaceBetween: 10 },
        990: { slidesPerView: 1.2, spaceBetween: 10 },
        580: { slidesPerView: 2.2, spaceBetween: 10 },
        350: { slidesPerView: 2.2 },
      },
      on: {
        init: function () {
          updateNavigationButtons(this);
          // Keep duplicate slides out of tab order so Tab doesn't cycle through repeated products
          sliderEl.querySelectorAll('.swiper-slide-duplicate a, .swiper-slide-duplicate [tabindex="0"]').forEach((el) => {
            el.setAttribute('tabindex', '-1');
          });
        },
        slideChange: function () {
          updateNavigationButtons(this);
        },
        resize: function () {
          updateNavigationButtons(this);
        },
      },
    });

    // Get navigation container
    const navContainer = wrapper.querySelector('.predictive-search-navigation');
    
    // Prevent navigation container from causing focus loss
    if (navContainer) {
      navContainer.addEventListener('mousedown', function (e) {
        // Keep focus inside predictive search; prevent mousedown from blurring input
        const target = e.target.closest('.predictive-search-nav-button');
        if (target) {
          e.preventDefault();
          e.stopPropagation();
        }
      });
    }

    // Add click handlers for navigation buttons
    if (prevButton) {
      prevButton.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        predictiveSwiper.slidePrev();
        return false;
      });
    }

    if (nextButton) {
      nextButton.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        predictiveSwiper.slideNext();
        return false;
      });
    }

    updateNavigationButtons(predictiveSwiper);
    wrapper.dataset.predictiveSwiperInitialized = 'true';
  });
}

window.initPredictiveSearchProductSliders = initPredictiveSearchProductSliders;

document.addEventListener('DOMContentLoaded', function () {
  initPredictiveSearchProductSliders();
});

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.isSelectingResult = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.predictiveSearchResults?.addEventListener('pointerdown', this.onResultPointerDown.bind(this));
    this.predictiveSearchResults?.addEventListener('click', this.onResultClick.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.isSelectingResult) {
        this.isSelectingResult = false;
        return;
      }

      const activeElement = document.activeElement;
      const hasResults = this.getAttribute('results') === 'true' && this.isOpen;
      const isMobile = window.matchMedia('(max-width: 990px)').matches;
      const isLargeDesktop = window.innerWidth >= 1250;

      // On mobile and large desktop: keep dropdown open when results are showing.
      // It will be closed explicitly via close button, overlay, or clearing the term.
      if (hasResults && (isMobile)) return; //|| isLargeDesktop

      // Don't close if:
      // 1. Focus is still within the predictive search element
      // 2. Focus is on navigation buttons or their container
      // 3. The active element is part of the predictive search results
      if (!this.contains(activeElement)) {
        const isNavButton = activeElement?.classList?.contains('predictive-search-nav-button') ||
                           activeElement?.closest('.predictive-search-navigation');
        const isInResults = activeElement?.closest('#predictive-search-results') ||
                           activeElement?.closest('.predictive-search');
        
        if (!isNavButton && !isInResults) {
          this.close();
        }
      }
    }, 100);
  }

  onResultPointerDown(event) {
    if (event.target.closest('.predictive-search__item--link-with-thumbnail')) {
      this.isSelectingResult = true;
    }
  }

  onResultClick(event) {
    const link = event.target.closest('.predictive-search__item--link-with-thumbnail');
    if (!link || !link.href) return;

    event.preventDefault();
    this.isSelectingResult = false;
    window.location.assign(link.href);
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

fetch(
  `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search&resources[limit]=10&resources[options][fields]=title,tag,product_type,variants.title,body`,
  {
    signal: this.abortController.signal,
  }
)
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    if (window.initMainSwiperBoxes) {
      window.initMainSwiperBoxes(this.predictiveSearchResults);
    }
    if (window.initPredictiveSearchProductSliders) {
      window.initPredictiveSearchProductSliders(this.predictiveSearchResults);
    }

    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
